import { Node } from '@tiptap/core';
import React, { useEffect } from 'react';

import { NodeViewWrapper } from '@tiptap/react';
import { ReactNodeViewRenderer } from '@tiptap/react';

import { Plus, Trash2 } from 'lucide-react';

export default function RadioGroupView({ node, updateAttributes }) {
  const { id, label, options, selected } = node.attrs;
  const onDragStart = (e) => {
    const payload = JSON.stringify({
      type: 'editor-drag',
      nodeType: 'radioGroup',
      id: node.attrs.id,
    });

    e.dataTransfer.setData('application/x-rp-editor', payload);
    e.dataTransfer.setData('text/plain', payload);
    e.dataTransfer.effectAllowed = 'move';
  };
  useEffect(() => {
    if (!id) {
      updateAttributes({ id: crypto.randomUUID() });
    }
  }, [id]);
  const updateOption = (index, value) => {
    const newOptions = [...options];
    newOptions[index] = value;
    updateAttributes({ options: newOptions });
  };

  const addOption = () => {
    updateAttributes({
      options: [...options, `Option ${options.length + 1}`],
    });
  };

  const removeOption = (index) => {
    const newOptions = options.filter((_, i) => i !== index);

    // reset selected if removed
    let newSelected = selected;
    if (selected === options[index]) {
      newSelected = null;
    }

    updateAttributes({
      options: newOptions,
      selected: newSelected,
    });
  };

  const handleSelect = (value) => {
    updateAttributes({ selected: value });
  };

  return (
    <NodeViewWrapper
      draggable
      onDragStart={onDragStart}
      id={id} // ✅ CRITICAL
      data-placeholder-id={id}
      className="my-4 rounded-xl border border-slate-200 p-4 bg-white shadow-sm space-y-3"
    >
      {/* LABEL EDIT */}
      <input
        value={label}
        onChange={(e) => updateAttributes({ label: e.target.value })}
        placeholder="Enter question..."
        className="w-full text-sm font-semibold text-slate-700 border-b border-dashed outline-none focus:border-[#375AE7] pb-1"
      />

      {/* OPTIONS */}
      <div className="space-y-2">
        {options.map((opt, i) => (
          <div key={i} className="flex items-center gap-2 group">
            {/* RADIO */}
            <div
              onClick={() => handleSelect(opt)}
              className={`h-4 w-4 rounded-full border flex items-center justify-center cursor-pointer ${
                selected === opt
                  ? 'border-[#375AE7] bg-[#375AE7]'
                  : 'border-slate-300'
              }`}
            >
              {selected === opt && (
                <div className="h-2 w-2 bg-white rounded-full" />
              )}
            </div>

            {/* OPTION INPUT */}
            <input
              value={opt}
              onChange={(e) => updateOption(i, e.target.value)}
              className="flex-1 text-sm border-b border-transparent focus:border-slate-300 outline-none"
            />

            {/* DELETE */}
            <button
              onClick={() => removeOption(i)}
              className="opacity-0 group-hover:opacity-100 transition"
            >
              <Trash2 className="h-4 w-4 text-red-400 hover:text-red-600" />
            </button>
          </div>
        ))}
      </div>

      {/* ADD OPTION */}
      <button
        onClick={addOption}
        className="flex items-center gap-1 text-xs text-[#375AE7] hover:underline"
      >
        <Plus className="h-3 w-3" />
        Add option
      </button>
    </NodeViewWrapper>
  );
}
export const RadioGroup = Node.create({
  name: 'radioGroup',
  group: 'block',
  atom: true,

  addAttributes() {
    return {
      id: { default: null },
      label: { default: '' },
      options: { default: [] },
      selected: { default: null },
    };
  },

  parseHTML() {
    return [{ tag: 'radio-group' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['radio-group', HTMLAttributes];
  },

  addNodeView() {
    return ReactNodeViewRenderer(RadioGroupView);
  },

  addCommands() {
    return {
      insertRadioGroup:
        (config = {}) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: {
              id: crypto.randomUUID(), // ✅ ADD THIS
              label: config.label || '',
              options: config.options || ['Option 1'],
              selected: null,
            },
          });
        },
    };
  },
});
